import { useReducer } from 'react';

import {
  DEFAULT_PLAYTEST_BUTTON_LABEL,
  derivePlaytestEventName,
  PLAYTEST_EVENTS_MAX,
  PLAYTEST_QUESTIONS_PER_EVENT_MAX,
  NPS_FIELD_DEFAULTS,
  type PlaytestEventWithQuestions,
  type PlaytestQuestion,
} from '@playerbase/types';

import {
  makeBlankField,
  SELECT_OPTION_MAX,
  type BuilderError as FieldBuilderError,
  type BuilderField,
  type BuilderFieldKind,
} from './use-form-builder-state.js';

// ---------------------------------------------------------------------------
// Playtest builder state.
//
// Mirrors the feedback-form builder per event: each event has its own
// fields array shaped like BuilderField (from use-form-builder-state.ts).
// We don't reuse useFormBuilderState directly because we have N nested
// field lists (one per event); instead we hold the same BuilderField
// shape under our own reducer + expose per-event adapters that look like
// UseFormBuilderState to the existing FieldsCard / FieldRow components.
//
// The reducer is pure - no RPCs, no Realtime. The page seeds state from
// the server tree, dispatches updates here, and pushes back via
// replace_playtest_events on save.
// ---------------------------------------------------------------------------

export type BuilderQuestionKind = BuilderFieldKind;
export type BuilderQuestion = BuilderField;

export interface BuilderEvent {
  clientId: string;
  serverId: string | null;
  // Portal-facing label the studio enters first. Required; the SDK
  // identifier (`name`) is derived from this until the studio edits it.
  label: string;
  // SDK-facing identifier. Constrained to a JS-style identifier shape
  // (^[A-Za-z_][A-Za-z0-9_]*$) so it pastes straight into game code.
  name: string;
  description: string | null;
  // Tester-facing copy. Headline doubles as DM bubble text and modal
  // title; buttonLabel is the DM button copy (defaults to "Share feedback").
  headline: string;
  buttonLabel: string;
  questions: BuilderQuestion[];
}

export interface BuilderMetadata {
  name: string;
  internalDescription: string;
}

export type BuilderError = FieldBuilderError | 'event_cap_reached' | 'question_cap_reached';

export interface BuilderState {
  metadata: BuilderMetadata;
  events: BuilderEvent[];
  lastError: BuilderError | null;
}

export const EVENT_CAP = PLAYTEST_EVENTS_MAX;
export const QUESTION_CAP = PLAYTEST_QUESTIONS_PER_EVENT_MAX;

type Action =
  | { type: 'reset'; state: BuilderState }
  | { type: 'updateMetadata'; patch: Partial<BuilderMetadata> }
  | { type: 'addEvent'; clientId: string }
  | { type: 'removeEvent'; clientId: string }
  | { type: 'reorderEvents'; fromIndex: number; toIndex: number }
  | {
      type: 'updateEvent';
      clientId: string;
      patch: Partial<Pick<BuilderEvent, 'name' | 'label' | 'description' | 'headline' | 'buttonLabel'>>;
    }
  | { type: 'addQuestion'; eventClientId: string; kind: BuilderQuestionKind; clientId: string }
  | { type: 'removeQuestion'; eventClientId: string; clientId: string }
  | { type: 'reorderQuestions'; eventClientId: string; fromIndex: number; toIndex: number }
  | { type: 'updateFieldTitle'; eventClientId: string; clientId: string; value: string }
  | { type: 'updateFieldDescription'; eventClientId: string; clientId: string; value: string | null }
  | { type: 'updateFieldRequired'; eventClientId: string; clientId: string; value: boolean }
  | {
      type: 'setRatingDisplay';
      eventClientId: string;
      clientId: string;
      display: { mode: 'emoji'; emoji: string } | undefined;
    }
  | {
      type: 'setBinaryLabel';
      eventClientId: string;
      clientId: string;
      which: 'true' | 'false';
      value: string | undefined;
    }
  | {
      type: 'replaceSelectOption';
      eventClientId: string;
      clientId: string;
      index: number;
      value: string;
    }
  | { type: 'addSelectOption'; eventClientId: string; clientId: string }
  | { type: 'removeSelectOption'; eventClientId: string; clientId: string; index: number }
  | {
      type: 'setSelectMaxValues';
      eventClientId: string;
      clientId: string;
      maxValues: number | undefined;
    }
  | { type: 'applyServerIds'; eventIds: Array<{ clientId: string; serverId: string }> }
  | { type: 'clearError' };

// ---------------------------------------------------------------------------
// Helpers (pure).
// ---------------------------------------------------------------------------

export function makeBlankEvent(clientId: string): BuilderEvent {
  return {
    clientId,
    serverId: null,
    label: '',
    name: '',
    description: null,
    headline: '',
    buttonLabel: DEFAULT_PLAYTEST_BUTTON_LABEL,
    questions: [],
  };
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to) return arr;
  if (from < 0 || from >= arr.length) return arr;
  if (to < 0 || to >= arr.length) return arr;
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  if (item === undefined) return arr;
  next.splice(to, 0, item);
  return next;
}

function patchEvent(
  events: BuilderEvent[],
  clientId: string,
  apply: (event: BuilderEvent) => BuilderEvent,
): BuilderEvent[] {
  let touched = false;
  const next = events.map((e) => {
    if (e.clientId !== clientId) return e;
    touched = true;
    return apply(e);
  });
  return touched ? next : events;
}

function patchQuestion(
  events: BuilderEvent[],
  eventClientId: string,
  questionClientId: string,
  apply: (question: BuilderQuestion) => BuilderQuestion,
): BuilderEvent[] {
  return patchEvent(events, eventClientId, (event) => {
    let touched = false;
    const questions = event.questions.map((q) => {
      if (q.clientId !== questionClientId) return q;
      touched = true;
      return apply(q);
    });
    return touched ? { ...event, questions } : event;
  });
}

// ---------------------------------------------------------------------------
// Reducer.
//
// Mirrors useFormBuilderState's per-action shape so the field-level
// behaviour (option add/remove, rating display, binary labels, etc.) is
// identical between feedback-form and playtest builders.
// ---------------------------------------------------------------------------

export function builderReducer(state: BuilderState, action: Action): BuilderState {
  switch (action.type) {
    case 'reset':
      return action.state;

    case 'updateMetadata':
      return { ...state, metadata: { ...state.metadata, ...action.patch }, lastError: null };

    case 'addEvent': {
      if (state.events.length >= EVENT_CAP) {
        return { ...state, lastError: 'event_cap_reached' };
      }
      return { ...state, events: [...state.events, makeBlankEvent(action.clientId)], lastError: null };
    }

    case 'removeEvent': {
      const next = state.events.filter((e) => e.clientId !== action.clientId);
      if (next.length === state.events.length) return state;
      return { ...state, events: next, lastError: null };
    }

    case 'reorderEvents': {
      const next = moveItem(state.events, action.fromIndex, action.toIndex);
      if (next === state.events) return state;
      return { ...state, events: next, lastError: null };
    }

    case 'updateEvent': {
      const events = patchEvent(state.events, action.clientId, (event) => {
        const next = { ...event, ...action.patch };
        // Keep the SDK identifier (`name`) in sync with the portal label
        // for as long as the studio hasn't manually edited it. Heuristic:
        // if the current name is empty OR matches the derivation of the
        // PREVIOUS label, the studio is still happy with the auto-name -
        // recompute. Once they diverge from the derivation, we leave name
        // alone. If the patch itself includes a `name`, that wins outright.
        if (action.patch.label !== undefined && action.patch.name === undefined) {
          const previousDerived = derivePlaytestEventName(event.label);
          const stillAuto = event.name === '' || event.name === previousDerived;
          if (stillAuto) {
            next.name = derivePlaytestEventName(action.patch.label);
          }
        }
        return next;
      });
      return { ...state, events, lastError: null };
    }

    case 'addQuestion': {
      let blocked = false;
      let npsConflict = false;
      const events = patchEvent(state.events, action.eventClientId, (event) => {
        if (event.questions.length >= QUESTION_CAP) {
          blocked = true;
          return event;
        }
        if (action.kind === 'nps' && event.questions.some((q) => q.kind === 'nps')) {
          npsConflict = true;
          return event;
        }
        return {
          ...event,
          questions: [...event.questions, makeBlankField(action.kind, action.clientId)],
        };
      });
      if (blocked) return { ...state, lastError: 'question_cap_reached' };
      if (npsConflict) return { ...state, lastError: 'nps_already_present' };
      return { ...state, events, lastError: null };
    }

    case 'removeQuestion': {
      const events = patchEvent(state.events, action.eventClientId, (event) => ({
        ...event,
        questions: event.questions.filter((q) => q.clientId !== action.clientId),
      }));
      return { ...state, events, lastError: null };
    }

    case 'reorderQuestions': {
      const events = patchEvent(state.events, action.eventClientId, (event) => {
        const next = moveItem(event.questions, action.fromIndex, action.toIndex);
        if (next === event.questions) return event;
        return { ...event, questions: next };
      });
      return { ...state, events, lastError: null };
    }

    case 'updateFieldTitle':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => ({
          ...q,
          title: action.value,
        })),
        lastError: null,
      };

    case 'updateFieldDescription':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => ({
          ...q,
          description: action.value === null ? null : action.value,
        })),
        lastError: null,
      };

    case 'updateFieldRequired':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => ({
          ...q,
          required: action.value,
        })),
        lastError: null,
      };

    case 'setRatingDisplay':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => {
          if (q.kind !== 'rating') return q;
          return {
            ...q,
            config: action.display === undefined ? {} : { display: action.display },
          };
        }),
        lastError: null,
      };

    case 'setBinaryLabel':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => {
          if (q.kind !== 'binary') return q;
          const config = q.config as { trueLabel?: string; falseLabel?: string };
          const key = action.which === 'true' ? 'trueLabel' : 'falseLabel';
          const next: { trueLabel?: string; falseLabel?: string } = { ...config };
          if (action.value === undefined || action.value.length === 0) delete next[key];
          else next[key] = action.value;
          return { ...q, config: next };
        }),
        lastError: null,
      };

    case 'replaceSelectOption':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => {
          if (q.kind !== 'select') return q;
          const config = q.config as { options: string[]; maxValues?: number };
          const options = config.options.slice();
          if (action.index < 0 || action.index >= options.length) return q;
          options[action.index] = action.value;
          return {
            ...q,
            config:
              config.maxValues !== undefined
                ? { options, maxValues: config.maxValues }
                : { options },
          };
        }),
        lastError: null,
      };

    case 'addSelectOption':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => {
          if (q.kind !== 'select') return q;
          const config = q.config as { options: string[]; maxValues?: number };
          if (config.options.length >= SELECT_OPTION_MAX) return q;
          const options = [...config.options, ''];
          return {
            ...q,
            config:
              config.maxValues !== undefined
                ? { options, maxValues: config.maxValues }
                : { options },
          };
        }),
        lastError: null,
      };

    case 'removeSelectOption':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => {
          if (q.kind !== 'select') return q;
          const config = q.config as { options: string[]; maxValues?: number };
          if (config.options.length <= 1) {
            return { ...q, config: { options: [''] } };
          }
          if (action.index < 0 || action.index >= config.options.length) return q;
          const options = config.options.slice();
          options.splice(action.index, 1);
          const nextMax =
            config.maxValues !== undefined && config.maxValues > options.length
              ? options.length
              : config.maxValues;
          return {
            ...q,
            config:
              nextMax === undefined ? { options } : { options, maxValues: nextMax },
          };
        }),
        lastError: null,
      };

    case 'setSelectMaxValues':
      return {
        ...state,
        events: patchQuestion(state.events, action.eventClientId, action.clientId, (q) => {
          if (q.kind !== 'select') return q;
          const config = q.config as { options: string[]; maxValues?: number };
          if (action.maxValues === undefined || action.maxValues <= 1) {
            return { ...q, config: { options: config.options } };
          }
          const ceiling = Math.min(config.options.length, 25);
          const clamped = Math.max(1, Math.min(action.maxValues, ceiling));
          return {
            ...q,
            config:
              clamped <= 1
                ? { options: config.options }
                : { options: config.options, maxValues: clamped },
          };
        }),
        lastError: null,
      };

    case 'applyServerIds': {
      if (action.eventIds.length === 0) return state;
      const map = new Map(action.eventIds.map((i) => [i.clientId, i.serverId]));
      let touched = false;
      const events = state.events.map((e) => {
        const serverId = map.get(e.clientId);
        if (serverId === undefined) return e;
        if (e.serverId === serverId) return e;
        touched = true;
        return { ...e, serverId };
      });
      if (!touched) return state;
      return { ...state, events };
    }

    case 'clearError':
      if (state.lastError === null) return state;
      return { ...state, lastError: null };
  }
}

// ---------------------------------------------------------------------------
// Hook.
// ---------------------------------------------------------------------------

export interface UsePlaytestBuilderState {
  state: BuilderState;
  reset(state: BuilderState): void;
  updateMetadata(patch: Partial<BuilderMetadata>): void;
  addEvent(clientId: string): void;
  removeEvent(clientId: string): void;
  reorderEvents(fromIndex: number, toIndex: number): void;
  updateEvent(
    clientId: string,
    patch: Partial<Pick<BuilderEvent, 'name' | 'label' | 'description' | 'headline' | 'buttonLabel'>>,
  ): void;
  addQuestion(eventClientId: string, kind: BuilderQuestionKind, clientId: string): void;
  removeQuestion(eventClientId: string, clientId: string): void;
  reorderQuestions(eventClientId: string, fromIndex: number, toIndex: number): void;
  updateFieldTitle(eventClientId: string, clientId: string, value: string): void;
  updateFieldDescription(eventClientId: string, clientId: string, value: string | null): void;
  updateFieldRequired(eventClientId: string, clientId: string, value: boolean): void;
  setRatingDisplay(
    eventClientId: string,
    clientId: string,
    display: { mode: 'emoji'; emoji: string } | undefined,
  ): void;
  setBinaryLabel(
    eventClientId: string,
    clientId: string,
    which: 'true' | 'false',
    value: string | undefined,
  ): void;
  replaceSelectOption(eventClientId: string, clientId: string, index: number, value: string): void;
  addSelectOption(eventClientId: string, clientId: string): void;
  removeSelectOption(eventClientId: string, clientId: string, index: number): void;
  setSelectMaxValues(
    eventClientId: string,
    clientId: string,
    maxValues: number | undefined,
  ): void;
  applyServerIds(eventIds: Array<{ clientId: string; serverId: string }>): void;
  clearError(): void;
}

export function usePlaytestBuilderState(initial: BuilderState): UsePlaytestBuilderState {
  const [state, dispatch] = useReducer(builderReducer, initial);
  return {
    state,
    reset: (s) => dispatch({ type: 'reset', state: s }),
    updateMetadata: (patch) => dispatch({ type: 'updateMetadata', patch }),
    addEvent: (clientId) => dispatch({ type: 'addEvent', clientId }),
    removeEvent: (clientId) => dispatch({ type: 'removeEvent', clientId }),
    reorderEvents: (from, to) =>
      dispatch({ type: 'reorderEvents', fromIndex: from, toIndex: to }),
    updateEvent: (clientId, patch) => dispatch({ type: 'updateEvent', clientId, patch }),
    addQuestion: (eventClientId, kind, clientId) =>
      dispatch({ type: 'addQuestion', eventClientId, kind, clientId }),
    removeQuestion: (eventClientId, clientId) =>
      dispatch({ type: 'removeQuestion', eventClientId, clientId }),
    reorderQuestions: (eventClientId, from, to) =>
      dispatch({ type: 'reorderQuestions', eventClientId, fromIndex: from, toIndex: to }),
    updateFieldTitle: (eventClientId, clientId, value) =>
      dispatch({ type: 'updateFieldTitle', eventClientId, clientId, value }),
    updateFieldDescription: (eventClientId, clientId, value) =>
      dispatch({ type: 'updateFieldDescription', eventClientId, clientId, value }),
    updateFieldRequired: (eventClientId, clientId, value) =>
      dispatch({ type: 'updateFieldRequired', eventClientId, clientId, value }),
    setRatingDisplay: (eventClientId, clientId, display) =>
      dispatch({ type: 'setRatingDisplay', eventClientId, clientId, display }),
    setBinaryLabel: (eventClientId, clientId, which, value) =>
      dispatch({ type: 'setBinaryLabel', eventClientId, clientId, which, value }),
    replaceSelectOption: (eventClientId, clientId, index, value) =>
      dispatch({ type: 'replaceSelectOption', eventClientId, clientId, index, value }),
    addSelectOption: (eventClientId, clientId) =>
      dispatch({ type: 'addSelectOption', eventClientId, clientId }),
    removeSelectOption: (eventClientId, clientId, index) =>
      dispatch({ type: 'removeSelectOption', eventClientId, clientId, index }),
    setSelectMaxValues: (eventClientId, clientId, maxValues) =>
      dispatch({ type: 'setSelectMaxValues', eventClientId, clientId, maxValues }),
    applyServerIds: (eventIds) => dispatch({ type: 'applyServerIds', eventIds }),
    clearError: () => dispatch({ type: 'clearError' }),
  };
}

// ---------------------------------------------------------------------------
// Seed helpers used by the page on first load + on realtime reseed.
// ---------------------------------------------------------------------------

function questionFromServer(q: PlaytestQuestion): BuilderQuestion {
  // BuilderField uses `title` for the prompt; the playtest column is named
  // `prompt` upstream but the in-memory shape needs to match BuilderField
  // so the existing FieldRow / per-kind config components can render it.
  return {
    clientId: q.id,
    serverId: q.id,
    kind: q.kind,
    title: q.prompt,
    description: q.helpText,
    required: q.required,
    config: q.config as BuilderField['config'],
  };
}

export function builderStateFromServer(
  playtest: { name: string; internalDescription: string | null },
  tree: PlaytestEventWithQuestions[],
): BuilderState {
  return {
    metadata: {
      name: playtest.name,
      internalDescription: playtest.internalDescription ?? '',
    },
    events: tree.map((event) => ({
      clientId: event.id,
      serverId: event.id,
      name: event.name,
      label: event.label,
      description: event.description,
      headline: event.headline,
      buttonLabel: event.buttonLabel,
      questions: event.questions.map(questionFromServer),
    })),
    lastError: null,
  };
}

// NPS_FIELD_DEFAULTS re-export to keep callers consistent with the form
// builder's helper. The reducer uses makeBlankField under the hood which
// already consults the defaults.
export { NPS_FIELD_DEFAULTS };
